import React from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, Rocket, ArrowRight, Target, Users, Shield, Zap } from 'lucide-react';
import { useSection } from '../contexts/HeaderThemeContext';

export default function Home() {
  // Register sections with their themes
  const heroRef = useSection('hero', 'light');
  const problemRef = useSection('problem', 'light');
  const featuresRef = useSection('features', 'light');
  const ctaRef = useSection('cta', 'dark');

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative py-20 overflow-hidden bg-linear-to-r from-blue-600/10 to-indigo-600/10"
      >
        <div className="absolute inset-0 bg-linear-to-r from-blue-600/5 to-indigo-600/5" />
        <div className="relative container max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 mb-8 bg-blue-100 text-blue-800 border border-blue-200 rounded-full text-sm font-medium">
            <Sparkles className="h-4 w-4 mr-2" />
            Montana Community Management Platform
          </div>

          <h1 className="text-5xl md:text-7xl font-bold mb-8 text-slate-900 leading-tight">
            Transform Your Montana Community Into a
            <span className="block text-blue-600 mt-2">Connected Ecosystem</span>
          </h1>

          <p className="text-xl md:text-2xl text-slate-600 mb-12 max-w-4xl mx-auto leading-relaxed">
            Join Montana communities revolutionizing governance, finance, and resident services with
            <strong className="text-slate-800"> one integrated platform</strong> designed
            specifically for rural communities.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-16">
            <Link to="/onboarding">
              <button className="bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-lg px-8 py-4 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all transform hover:scale-105">
                <Rocket className="h-5 w-5 mr-2 inline" />
                Start Your Transformation
              </button>
            </Link>
            <Link to="/pricing/montana/406-parcels">
              <button className="border-2 border-slate-300 hover:border-blue-600 text-slate-700 hover:text-blue-600 text-lg px-8 py-4 rounded-lg font-semibold transition-all hover:bg-blue-50">
                Explore Features <ArrowRight className="h-5 w-5 ml-2 inline" />
              </button>
            </Link>
          </div>

          {/* Hero Stats */}
          <div className="flex flex-wrap justify-center gap-8 max-w-4xl mx-auto">
            <div className="text-center p-4">
              <div className="text-4xl font-bold text-indigo-600">90</div>
              <div className="text-sm text-slate-600 font-medium">Day Implementation</div>
            </div>
            <div className="text-center p-4">
              <div className="text-4xl font-bold text-purple-600">24/7</div>
              <div className="text-sm text-slate-600 font-medium">Resident Access</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section ref={problemRef} className="py-20 bg-white">
        <div className="container max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
              Why Montana Communities Choose <span className="text-blue-600">ToolboxLogic</span>
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              Rural communities face unique challenges. Our platform is built specifically for
              Montana's geographic, regulatory, and operational requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-white border-2 border-blue-100 rounded-xl p-8 hover:shadow-lg hover:border-blue-200 transition-all">
              <div className="h-16 w-16 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                <Target className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Montana Landowner Association Framework
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Purpose-built for Montana nonprofit landowner associations. Streamlines CC&R
                compliance, bylaws management, and Park County coordination. Built for real Montana
                communities like GLA.
              </p>
            </div>

            <div className="bg-white border-2 border-green-100 rounded-xl p-8 hover:shadow-lg hover:border-green-200 transition-all">
              <div className="h-16 w-16 bg-green-100 rounded-xl flex items-center justify-center mb-6">
                <Users className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                Rural Landowner Association Focus
              </h3>
              <p className="text-slate-600 leading-relaxed">
                Designed for communities managing large parcels, seasonal residents, road
                maintenance, project approvals, and unique Montana challenges like water rights and
                resource sharing.
              </p>
            </div>

            <div className="bg-white border-2 border-purple-100 rounded-xl p-8 hover:shadow-lg hover:border-purple-200 transition-all">
              <div className="h-16 w-16 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                <Shield className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">Dedicated Community Support</h3>
              <p className="text-slate-600 leading-relaxed">
                Dedicated support team who understands local challenges. Implementation, training,
                and ongoing support from people who get it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section ref={featuresRef} className="py-20 bg-slate-50">
        <div className="container max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight text-slate-900 mb-6">
              Everything Your Community Needs
            </h2>
            <p className="text-xl text-slate-600 leading-relaxed">
              From digital governance to financial management, we've integrated every tool Montana
              communities need into one powerful platform.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="bg-white rounded-xl p-8 shadow-xs hover:shadow-lg transition-all border border-slate-200">
              <div className="h-14 w-14 bg-linear-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mb-6">
                <span className="text-white text-2xl">💳</span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Digital Payments</h3>
              <p className="text-slate-600 leading-relaxed">
                Streamlined fee collection, automatic reminders, and transparent financial
                reporting.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-xs hover:shadow-lg transition-all border border-slate-200">
              <div className="h-14 w-14 bg-linear-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mb-6">
                <span className="text-white text-2xl">🗳️</span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Digital Governance</h3>
              <p className="text-slate-600 leading-relaxed">
                Secure voting, proposal management, and board meeting coordination all in one place.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-xs hover:shadow-lg transition-all border border-slate-200">
              <div className="h-14 w-14 bg-linear-to-br from-purple-500 to-violet-600 rounded-lg flex items-center justify-center mb-6">
                <span className="text-white text-2xl">🛠️</span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Vendor Management</h3>
              <p className="text-slate-600 leading-relaxed">
                Coordinate maintenance, track work orders, and manage contractor relationships.
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-xs hover:shadow-lg transition-all border border-slate-200">
              <div className="h-14 w-14 bg-linear-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mb-6">
                <span className="text-white text-2xl">🤝</span>
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-3">Group Buying</h3>
              <p className="text-slate-600 leading-relaxed">
                Leverage collective purchasing power for better pricing on community essentials.
              </p>
            </div>
          </div>

          <div className="text-center">
            <Link to="/pricing/montana/406-parcels">
              <button className="bg-linear-to-r from-blue-600 to-indigo-700 hover:bg-blue-800 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl">
                <Zap className="h-5 w-5 mr-2 inline" />
                See All Features
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section ref={ctaRef} className="py-20 bg-linear-to-r from-blue-600 to-indigo-700">
        <div className="container max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl font-bold text-white mb-6">
              Ready to Transform Your Montana Community?
            </h2>
            <p className="text-xl text-blue-100 mb-10 leading-relaxed">
              Revolutionize your Montana community's governance, finance, and resident services with
              our integrated platform designed exclusively for rural communities.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <Link to="/onboarding">
                <button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 rounded-lg font-semibold text-lg transition-all shadow-lg hover:shadow-xl">
                  Get Started Today
                </button>
              </Link>
              <Link to="/pricing/montana/406-parcels">
                <button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-lg font-semibold text-lg transition-all">
                  View Pricing <ArrowRight className="h-5 w-5 ml-2 inline" />
                </button>
              </Link>
            </div>
            {/*
            <div className="mt-12 pt-8 border-t border-blue-500">
              <p className="text-blue-200 text-sm">
                ✨ <strong>Special Offer:</strong> Montana communities get 10% off first year implementation
              </p>
            </div>
            */}
          </div>
        </div>
      </section>
    </div>
  );
}
